import { Stack } from "@mui/material";
import {
  BottomSection,
  MainSection,
} from "Components/AnonymousCustomer/Payment/SectionLayout";
import { TermsAgreementText } from "Components/AnonymousCustomer/Payment/TermsAgreementText";
import { useStartCollectorPayment } from "Components/AnonymousCustomer/Payment/Collector/useStartCollectorPayment";
import { AmountPresetButtons } from "Components/Customer/Payment/AmountPresetButtons";
import { EditableAmountCard } from "Components/Customer/Payment/EditableAmountCard";
import { MerchantHeader } from "Components/Customer/Payment/MerchantHeader";
import { getPayButtonLabel } from "Components/Customer/Payment/PaymentForms/payButtonUtils";
import { PaymentAmountCard } from "Components/Customer/Payment/PaymentAmountCard";
import { TipSection } from "Components/Customer/Payment/PaymentForms/TipSection";
import { computeTipAmount } from "Components/Customer/Payment/PaymentForms/tipUtils";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import type { TipMode } from "Components/Shared/TipSelector/types";
import type { PayPointScanViewDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { getAmountPresetsForCurrency } from "Utils/AmountPresetUtils";
import { useState } from "react";

type Props = {
  payPoint: PayPointScanViewDto;
};

export const AnonymousCollectorStepOne = ({
  payPoint: {
    predefinedAmountCurrencyCode: currencyCode,
    predefinedAmount,
    name,
    publicID,
    tipsEnabled,
  },
}: Props) => {
  const [customAmount, setCustomAmount] = useState<number | undefined>(
    undefined,
  );
  const [tipMode, setTipMode] = useState<TipMode>("none");
  const [customTipAmount, setCustomTipAmount] = useState<number | undefined>(
    undefined,
  );

  const { t } = useResource();
  const { startPayment, isProcessing, errorMessage } =
    useStartCollectorPayment();

  // Every Collector paypoint carries a currency (BE-validated and backfilled);
  // a missing one is broken data — never guess a currency for a payment.
  if (!currencyCode) {
    return <ErrorText>{t(Resources.Errors.DataLoadingError)}</ErrorText>;
  }

  const hasPredefinedAmount =
    predefinedAmount !== null && predefinedAmount !== undefined;
  const amountPresets = getAmountPresetsForCurrency(currencyCode);

  const billAmount = hasPredefinedAmount
    ? predefinedAmount
    : (customAmount ?? 0);
  const tipAmount = tipsEnabled
    ? computeTipAmount(tipMode, customTipAmount, billAmount)
    : 0;

  const handleTipModeChange = (mode: TipMode) => {
    setTipMode(mode);
    if (mode !== "custom") {
      setCustomTipAmount(undefined);
    }
  };

  const handleContinue = () =>
    startPayment({
      publicID: publicID,
      amount: billAmount,
      tipAmount,
    });

  const isContinueDisabled =
    isProcessing ||
    (!hasPredefinedAmount && (customAmount == null || customAmount <= 0));

  return (
    <Stack gap={7} flexGrow={1}>
      <MainSection>
        <MerchantHeader creditorName={name} payPointPublicID={publicID} />
        <Stack gap={6}>
          {hasPredefinedAmount ? (
            <PaymentAmountCard
              billAmount={billAmount}
              tipAmount={tipAmount}
              currencyCode={currencyCode}
              arePayPointTipsEnabled={tipsEnabled}
            />
          ) : (
            <>
              <EditableAmountCard
                value={customAmount}
                currencyCode={currencyCode}
                onChange={setCustomAmount}
              />
              <AmountPresetButtons
                presets={amountPresets}
                currencyCode={currencyCode}
                onSelect={setCustomAmount}
              />
            </>
          )}
          {tipsEnabled && (
            <TipSection
              tipMode={tipMode}
              currencyCode={currencyCode}
              customTipAmount={customTipAmount}
              onTipModeChange={handleTipModeChange}
              onCustomTipAmountChange={setCustomTipAmount}
            />
          )}
        </Stack>
        <TermsAgreementText />
      </MainSection>
      <BottomSection>
        {errorMessage && (
          <ErrorText textAlign="center">{errorMessage}</ErrorText>
        )}
        <PrimaryButton
          fullWidth
          color="primary"
          onClick={handleContinue}
          disabled={isContinueDisabled}
          isLoading={isProcessing}
        >
          {getPayButtonLabel(t, billAmount + tipAmount, currencyCode)}
        </PrimaryButton>
      </BottomSection>
    </Stack>
  );
};
